import React, { useMemo, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Row,
  Col,
  Table,
} from 'antd';
import {
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';

import BorderlessButton from '../../../../common/buttons/BorderlessButton';
import OnTraccrButton from '../../../../common/buttons/OnTraccrButton';
import TableDeleteButton from '../TableDeleteButton';

import ShiftTableAddDrawer from './ShiftTableAddDrawer';

import {
  getRepeatText,
  formatDay,
  formatRange,
  getAllDayValues,
} from '../../../../helpers/time';

import { getIdMap } from '../../../../helpers/helpers';
import DisplayText from '../../../../common/text/DisplayText';
import { generateId, getHasColumnPermissions } from '../../../formHelpers';
import Permissions from '../../../../auth/Permissions';
import useCheckTableMaxRows from '../../../../common/hooks/useCheckTableMaxRows';
import { getCalculationTableColumn, getCustomTableColumn, LINE_NUMBER_COLUMN } from '../formFieldsHelpers';
import TableAttachmentCell from '../TableField/TableAttachmentCell';
import ScheduleDrawer from '../../../../schedule/ScheduleDrawer';
import { getDefaultShiftTimes } from '../../../../schedule/scheduleHelpers';
import { formSectionsPropType } from '../../../forms.propTypes';

const getShiftTimesText = (times, shift) => {
  if (!Array.isArray(times)) return {};
  const { repeat, repeatEndDate } = shift;
  const [startTime, endTime] = times ?? [];
  if (!startTime || !endTime) return {};
  const startDT = DateTime.fromMillis(startTime);
  const endDT = DateTime.fromMillis(endTime);
  const repeatEndDT = repeatEndDate ? DateTime.fromMillis(repeatEndDate) : null;
  const {
    isAllDay,
    isMultiDay,
  } = getAllDayValues(startDT, endDT);
  const rangeMode = isAllDay || isMultiDay;
  const repeatText = getRepeatText({ mode: repeat, datetime: startDT, endDate: repeatEndDT });
  let firstLine;
  if (rangeMode) {
    if (endDT.hasSame(startDT, 'day')) {
      firstLine = formatDay(startTime);
    } else {
      firstLine = formatRange(
        [startTime, endTime],
        isAllDay ? DateTime.DATE_MED : DateTime.DATETIME_MED,
      );
    }
  } else {
    firstLine = `${formatDay(startTime)} ${formatRange([startTime, endTime], DateTime.TIME_SIMPLE)}`;
  }
  return { firstLine, repeatText };
};

const getColumnMap = ({
  requiredColumns,
  userIdMap,
  isDisplay,
  onValueChanged,
  onOpenFile,
  setCurrFileFieldId,
  setCurrAttachmentRowId,
  setOpenFileIndex,
  setCurrFieldId,
  fieldId,
  previewProps,
  responding,
}) => ({
  title: {
    title: <div className={requiredColumns && 'form-required-field'}>Title</div>,
    dataIndex: 'title',
  },
  description: {
    title: <div className={requiredColumns && 'form-required-field'}>Description</div>,
    dataIndex: 'description',
  },
  users: {
    title: <div className={requiredColumns && 'form-required-field'}>Users</div>,
    dataIndex: 'users',
    render: (users, record) => {
      if (Array.isArray(users)) {
        return users.map((userId) => userIdMap?.[userId]?.name)
          .filter((name) => name)
          .join(', ');
      }
      return users;
    },
  },
  dates: {
    title: <div className={requiredColumns && 'form-required-field'}>Dates</div>,
    dataIndex: 'times',
    render: (times, record) => {
      const relevantTimes = times || [record.startTime, record.endTime];
      const { firstLine, repeatText } = getShiftTimesText(relevantTimes, record);

      if (!repeatText) return firstLine;

      return <span>{firstLine}<br />{repeatText}</span>;
    },
  },
  attachments: {
    title: <div className={requiredColumns && 'form-required-field'}>Attachments</div>,
    dataIndex: 'attachments',
    width: 220,
    render: (attachments, record) => (
      <TableAttachmentCell
        value={attachments}
        isDisplay={isDisplay}
        onChange={(next) => onValueChanged(record.id, { attachments: next })}
        onOpenFile={onOpenFile}
        setCurrFileFieldId={setCurrFileFieldId}
        setCurrAttachmentRowId={setCurrAttachmentRowId}
        setOpenFileIndex={setOpenFileIndex}
        setCurrFieldId={setCurrFieldId}
        attachmentCellId={fieldId && record?.id ? `${fieldId}-${record.id}` : undefined}
        id={fieldId}
        rowId={record?.id}
        previewProps={previewProps}
        responding={responding}
      />
    ),
  },
});

const getColumns = ({
  onDelete,
  onEdit,
  onValueChanged,
  columns,
  isDisplay,
  requiredColumns,
  userIdMap,
  columnPermissions,
  userPositionId,
  onOpenFile,
  setCurrFileFieldId,
  setCurrAttachmentRowId,
  setOpenFileIndex,
  setCurrFieldId,
  fieldId,
  previewProps,
  responding,
  responses,
}) => {
  const cols = [LINE_NUMBER_COLUMN];
  const colMap = getColumnMap({
    requiredColumns,
    userIdMap,
    isDisplay,
    onValueChanged,
    onOpenFile,
    setCurrFileFieldId,
    setCurrAttachmentRowId,
    setOpenFileIndex,
    setCurrFieldId,
    fieldId,
    previewProps,
    responding,
  });
  columns.forEach((col) => {
    if (col.key in colMap) {
      cols.push(colMap[col.key]);
    } else if (col.isCustomText) {
      if (!getHasColumnPermissions({
        columnPermissions,
        column: col,
        userPositionId,
        userId: Permissions.id,
      })) {
        return;
      }
      cols.push(getCustomTableColumn(col, {
        width: 100,
        isDisplay,
        requiredColumns,
        responses,
        onChange: (record, value) => onValueChanged(record.id, { [col.key]: value }),
      }));
    } else if (col.isCalculation) {
      cols.push(getCalculationTableColumn(col, {
        isDisplay,
      }));
    }
  });
  if (!isDisplay) {
    cols.push({
      title: '',
      dataIndex: '',
      width: 100,
      render: (_, record) => (
        <BorderlessButton
          iconNode={<EditOutlined />}
          onClick={() => onEdit(record)}
        />
      ),
    });
    cols.push({
      title: '',
      dataIndex: '',
      width: 100,
      render: (_, record) => (
        <TableDeleteButton onConfirm={() => onDelete(record.id)} />
      ),
    });
  }

  return cols;
};

const decorateShift = (shift, userIdMap) => {
  const newShift = { ...(shift || {}) };

  const {
    userIds = [],
    users = [],
    times,
    startTime,
    endTime,
    version,
  } = newShift;

  const baseUserIds = [...userIds];
  if (version && Array.isArray(users)) {
    baseUserIds.push(...users);
  }

  const relevantUserIds = new Set(baseUserIds);
  const usersText = Array.from(relevantUserIds).map((userId) => (userIdMap?.[userId]?.name))
    .filter((name) => name)
    .join(', ');

  const relevantTimes = times || [startTime, endTime];
  const { firstLine, repeatText } = getShiftTimesText(relevantTimes, newShift);
  let dates = firstLine;
  if (repeatText) dates += `\n${repeatText}`;

  newShift.users = usersText;
  newShift.userIds = Array.from(relevantUserIds);
  newShift.dates = dates;
  newShift.shiftCategoryId = shift.categoryId || shift.shiftCategoryId;

  return newShift;
};

function ShiftTablePreview({
  title = null,
  columns = [],
  previewProps = {},
  setPreviewProps,
  isDisplay,
  id,
  setResponses,
  responses = {},
  responding = false,
  configProps = {},
  showCondensedView,
  projectId,
  projects = [],
  projectLocked,
  inAddDrawer,
  costcodes = [],
  sections = [],
  onOpenFile,
  setCurrFileFieldId,
  setCurrAttachmentRowId,
  setOpenFileIndex,
  setCurrFieldId,
}) {
  const {
    requiredColumns,
    fieldMappings,
    columnPermissions = {},
    reminderConfig,
  } = configProps ?? {};
  const values = previewProps.values || []; // For Responses
  const {
    values: previewSelected = [],
  } = previewProps;
  const {
    [id]: {
      values: responseSelected = [],
    } = {},
  } = responses;

  const selected = responding ? responseSelected : previewSelected;
  const selectedFormTemplate = useSelector((state) => state.forms.selectedFormTemplate);
  const selectedForm = useSelector((state) => state.forms.selectedForm);
  const users = useSelector((state) => state.users.users);
  const positionNames = useSelector((state) => state.settings.positionNames);
  const companySettings = useSelector((state) => state.settings.company.settings) ?? {};
  const userIdMap = useMemo(() => getIdMap(users), [users]);
  const positionNameMap = useMemo(() => getIdMap(positionNames, 'name'), [positionNames]);
  const userPositionId = useMemo(() => {
    const userPosition = userIdMap[Permissions.id]?.position;
    return positionNameMap[userPosition]?.id;
  }, [positionNameMap, userIdMap]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState();

  const decoratedSelected = useMemo(() => (
    selected.map((shift) => decorateShift(shift, userIdMap))
  ), [selected, userIdMap]);

  const ourDivisionId = useMemo(() => {
    if (selectedForm?.divisionId) {
      return selectedForm.divisionId;
    }
    if (selectedFormTemplate?.divisionId) {
      return selectedFormTemplate.divisionId;
    }
    return null;
  }, [selectedForm, selectedFormTemplate]);

  const generateAutofilledShift = useCallback(() => {
    const {
      title,
      description,
      dates,
    } = fieldMappings || {};

    const newShift = {
      version: 1,
    };
    if (title && responses[title]) {
      newShift.title = responses[title].value || responses[title].values?.[0]?.name;
    }
    if (description && responses[description]) {
      newShift.description = responses[description].value || responses[description].values?.[0]?.name;
    }
    if (dates && responses[dates]) {
      newShift.startTime = responses[dates].startTime;
      newShift.endTime = responses[dates].endTime;
    } else {
      const defaults = getDefaultShiftTimes(companySettings);
      if (defaults?.startTime && defaults?.endTime) {
        newShift.startTime = defaults.startTime;
        newShift.endTime = defaults.endTime;
      }
    }
    return newShift;
  }, [fieldMappings, responses, companySettings]);

  const showDrawer = useCallback(() => {
    setDrawerOpen(true);
    setSelectedShift(generateAutofilledShift());
  }, [generateAutofilledShift]);

  const hideDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedShift();
  }, []);

  const onSubmitChanges = useCallback((changedValues = {}) => {
    const normalizedValues = { ...changedValues };
    if (normalizedValues.files !== undefined) {
      normalizedValues.attachments = normalizedValues.files;
      delete normalizedValues.files;
    }
    const fullObject = decorateShift(
      { ...normalizedValues, version: selectedShift?.version },
      userIdMap,
    );
    const { costcodeId } = fullObject;
    const splitCostcodeId = costcodeId?.split('.');
    const isSplit = splitCostcodeId?.length === 2;
    const ourCostcodeId = isSplit ? splitCostcodeId[1] : costcodeId;
    let newSelected = decoratedSelected;
    if (!selectedShift?.id) {
      fullObject.id = generateId();
      fullObject.costcodeId = ourCostcodeId;
      newSelected = decoratedSelected.concat([fullObject]);
    } else {
      newSelected = decoratedSelected.map((item) => {
        if (item.id !== selectedShift.id) return item;
        const oldValue = { ...selectedShift };
        delete oldValue.userIds;
        return {
          ...oldValue,
          ...normalizedValues,
          userIds: fullObject.userIds,
          users: fullObject.users,
          dates: fullObject.dates,
          costcodeId: ourCostcodeId,
        };
      });
    }
    if (responding) {
      setResponses({
        ...responses,
        [id]: {
          ...(responses[id]),
          values: newSelected,
          columns,
        },
      });
    } else {
      setPreviewProps({
        ...previewProps,
        values: newSelected,
      });
    }
    hideDrawer();
  }, [
    decoratedSelected,
    previewProps,
    responses,
    setResponses,
    id,
    selectedShift,
    hideDrawer,
    userIdMap,
    selectedShift,
  ]);

  const onDelete = useCallback((itemId) => {
    const newSelected = decoratedSelected.filter((item) => item.id !== itemId);
    if (responding) {
      setResponses({
        ...responses,
        [id]: {
          ...(responses[id]),
          values: newSelected,
          columns,
        },
      });
    } else {
      setPreviewProps({
        ...previewProps,
        values: newSelected,
      });
    }
  }, [previewProps, decoratedSelected, id]);

  const onValueChanged = useCallback((changedId, newData = {}) => {
    const newSelected = decoratedSelected.map((item) => {
      if (item.id !== changedId) return item;
      return { ...item, ...newData };
    });
    if (responding) {
      setResponses({
        ...responses,
        [id]: {
          ...(responses[id]),
          values: newSelected,
          columns,
        },
      });
    } else {
      setPreviewProps({
        ...previewProps,
        values: newSelected,
      });
    }
  }, [
    decoratedSelected,
    previewProps,
    responses,
    setResponses,
    id,
    columns,
    responding,
  ]);

  const onEdit = useCallback((selectedRow) => {
    let newSelectedShift = selectedRow;

    // Schedule Drawer expects users to be an array of userIds and files instead of attachments
    if (newSelectedShift?.version) {
      newSelectedShift = {
        ...newSelectedShift,
        users: newSelectedShift.userIds,
        files: newSelectedShift.attachments,
      };
    }
    setSelectedShift(newSelectedShift);
    setDrawerOpen(true);
  }, []);

  const tableColumns = useMemo(() => (
    getColumns({
      onEdit,
      onDelete,
      onValueChanged,
      columns,
      isDisplay,
      requiredColumns,
      userIdMap,
      columnPermissions,
      userPositionId,
      onOpenFile,
      setCurrFileFieldId,
      setCurrAttachmentRowId,
      setOpenFileIndex,
      setCurrFieldId,
      fieldId: id,
      previewProps,
      responding,
      responses,
    })
  ), [
    columns,
    isDisplay,
    onDelete,
    onValueChanged,
    requiredColumns,
    userIdMap,
    columnPermissions,
    userPositionId,
    onEdit,
    onOpenFile,
    setCurrFileFieldId,
    setCurrAttachmentRowId,
    setOpenFileIndex,
    setCurrFieldId,
    id,
    previewProps,
    responding,
    responses,
  ]);

  const dataSource = useMemo(() => (
    isDisplay && !responding ? values : decoratedSelected
  ), [isDisplay, responding, values, decoratedSelected]);

  const {
    shouldAddButtonBeEnabled = true,
  } = useCheckTableMaxRows({
    configProps,
    currentRowsLength: dataSource?.length,
  });

  return (
    <>
      <Row
        style={{
          marginTop: showCondensedView ? 0 : 15,
          width: !inAddDrawer ? '100%' : 600,
        }}
      >
        <Row justify="space-between" style={{ width: '100%', marginBottom: 10 }}>
          <Col>
            {title}
          </Col>
          {!isDisplay && (
            <OnTraccrButton
              title="Add Row"
              onClick={showDrawer}
              icon={<PlusOutlined />}
              disabled={!shouldAddButtonBeEnabled}
            />
          )}
        </Row>
        { !showCondensedView || dataSource?.length ? (
          <Table
            className="form-table-preview"
            style={{ width: '100%', overflow: 'auto' }}
            columns={tableColumns}
            size="small"
            pagination={false}
            dataSource={dataSource}
            scroll={{ x: 'fit-content' }}
          />
        ) : (
          <DisplayText title="No Shifts Selected" style={{ marginBottom: 0 }} />
        )}
      </Row>
      {/* Shift Table is for backwards compatibility with old shift table */}
      <ShiftTableAddDrawer
        visible={drawerOpen && !selectedShift?.version}
        onClose={hideDrawer}
        onSubmit={onSubmitChanges}
        selectedShift={selectedShift}
        projects={projects}
        projectId={projectId}
        projectLocked={projectLocked}
        costcodes={costcodes}
      />
      {/* Schedule Drawer is for new shift table */}
      <ScheduleDrawer
        hideCalendar
        isShiftTable
        visible={drawerOpen && selectedShift?.version}
        onClose={hideDrawer}
        onSave={onSubmitChanges}
        onDelete={onDelete}
        shift={selectedShift}
        divisionId={ourDivisionId}
        projectId={projectId}
        projectLocked={projectLocked}
        isDisplay={isDisplay}
        reminderConfig={reminderConfig}
        sections={sections}
        formStyle={{
          padding: '0 0',
        }}
      />
    </>
  );
}

/* eslint-disable react/forbid-prop-types */
ShiftTablePreview.propTypes = {
  title: PropTypes.node,
  columns: PropTypes.array,
  previewProps: PropTypes.object,
  setPreviewProps: PropTypes.func.isRequired,
  isDisplay: PropTypes.bool,
  id: PropTypes.string.isRequired,
  setResponses: PropTypes.func.isRequired,
  responses: PropTypes.object,
  responding: PropTypes.bool,
  configProps: PropTypes.shape({
    requiredColumns: PropTypes.bool,
  }),
  showCondensedView: PropTypes.bool,
  projects: PropTypes.array,
  projectId: PropTypes.string,
  projectLocked: PropTypes.bool,
  inAddDrawer: PropTypes.bool,
  costcodes: PropTypes.array,
  sections: formSectionsPropType,
  onOpenFile: PropTypes.func,
  setCurrFileFieldId: PropTypes.func,
  setCurrAttachmentRowId: PropTypes.func,
  setOpenFileIndex: PropTypes.func,
  setCurrFieldId: PropTypes.func,
};

ShiftTablePreview.defaultProps = {
  title: null,
  columns: [],
  previewProps: {},
  isDisplay: false,
  responses: {},
  responding: false,
  configProps: {},
  showCondensedView: false,
  projects: [],
  projectId: null,
  projectLocked: false,
  inAddDrawer: false,
  costcodes: [],
  sections: [],
  onOpenFile: () => {},
  setCurrFileFieldId: () => {},
  setCurrAttachmentRowId: () => {},
  setOpenFileIndex: () => {},
  setCurrFieldId: () => {},
};

export default ShiftTablePreview;
